import type {
  BhgCallDashboardProvider,
  BhgClinicOption,
  BhgSubscriptionInfo,
  NotificationChannel,
} from "./types";
import { BHG_OFFERING_ID, type MonthlyScreenStatus } from "@constructorfleet/bhg-product-core";
import { createTTLCache } from "@constructorfleet/bhg-product-core";
import {
  convertBhgNotificationTimeToLocal,
  convertBhgNotificationTimeToUtc,
} from "@constructorfleet/bhg-product-core";
import { resolveApiUrl } from "./api-origin";

export type BhgCallRecord = {
  id: number;
  userId: number;
  startedAt: string;
  status: string;
  resultLabel: string | null;
  resultDate: string | null;
  callSid: string;
  transcript: string | null;
  recordingAvailable: boolean;
  recordingExpired: boolean;
  errorReason: string | null;
  voiceProvider: string | null;
  hangupParty?: "us" | "them" | "unknown" | null;
};

export type BhgScheduleSummary = {
  todayIsoDate: string;
  tomorrowIsoDate: string;
  today: {
    scheduledLabels: string[];
    unscheduledLabels: string[];
  };
  tomorrow: {
    scheduledLabels: string[];
    unscheduledLabels: string[];
  };
  monthlyScreen?: MonthlyScreenStatus | null;
  /**
   * Rule labels that satisfy the monthly screen. Optional because an older
   * server does not send it; absent means "any scheduled visit counts", which
   * is how the nudge behaved before the flag existed.
   */
  monthlyScreenLabels?: string[];
};

type CallsResponse = {
  ok: boolean;
  calls: BhgCallRecord[];
  scheduleSummary: BhgScheduleSummary;
  retentionDays?: number;
  updatedAt: string;
};

/**
 * A screen the patient recorded themselves, for visits the clinic never
 * announced over the phone. `reportedAt` is server-assigned — it is what makes
 * the entry contemporaneous rather than a claim made after the fact.
 */
export type BhgSelfReportedScreen = {
  id: number;
  screenDate: string;
  reportedAt: string;
  note: string | null;
  voidedAt: string | null;
};

export type BhgCallEvent = {
  id: number;
  callSid: string;
  createdAt: string;
  callStatus: string | null;
  callDurationSeconds: number | null;
  transcript: string | null;
  payloadKeys: string[];
};

type CallEventsResponse = {
  ok: boolean;
  callSid: string;
  events: BhgCallEvent[];
  updatedAt: string;
};

const SESSION_TOKEN_KEY = "cf-session-token";
const RECORDING_AVAILABILITY_TTL_MS = 5 * 60 * 1000;
const recordingAvailabilityCache = createTTLCache<
  string,
  { available: boolean; note: string | null }
>(RECORDING_AVAILABILITY_TTL_MS);
let testModeOverride: boolean | null = null;
const TEST_CALLS: BhgCallRecord[] = [
  {
    id: 1,
    userId: 1,
    startedAt: "2026-02-24 00:00 UTC",
    status: "completed",
    resultLabel: "Drug Screen",
    resultDate: "2026-02-24",
    callSid: "CA-2026-001",
    transcript: "You are scheduled for a drug screen today before 5 PM.",
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
    hangupParty: null,
  },
  {
    id: 2,
    userId: 1,
    startedAt: "2026-02-23 00:00 UTC",
    status: "completed",
    resultLabel: "Not Scheduled",
    resultDate: "2026-02-23",
    callSid: "CA-2026-002",
    transcript: "You are not scheduled to come into the treatment center on February 27th, 2026.",
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
    hangupParty: null,
  },
  {
    id: 3,
    userId: 1,
    startedAt: "2026-02-22 00:00 UTC",
    status: "completed",
    resultLabel: "Bottle Check",
    resultDate: "2026-02-22",
    callSid: "CA-2026-003",
    transcript: "Bottle Check is required today. Please report before clinic close.",
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
    hangupParty: null,
  },
  {
    id: 4,
    userId: 1,
    startedAt: "2026-02-21 00:00 UTC",
    status: "completed",
    resultLabel: "Drug Screen",
    resultDate: "2026-02-21",
    callSid: "CA-2026-004",
    transcript: "You are scheduled for a drug screen today before 3 PM.",
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
    hangupParty: null,
  },
  {
    id: 5,
    userId: 1,
    startedAt: "2026-02-20 00:00 UTC",
    status: "completed",
    resultLabel: "Not Scheduled",
    resultDate: "2026-02-20",
    callSid: "CA-2026-005",
    transcript: "You are not scheduled to come into the treatment center on February 27th, 2026.",
    recordingAvailable: true,
    recordingExpired: false,
    errorReason: null,
    voiceProvider: null,
    hangupParty: null,
  },
];

function getSessionToken() {
  return localStorage.getItem(SESSION_TOKEN_KEY) ?? "";
}

export function setBhgDashboardTestModeOverride(value: boolean | null) {
  testModeOverride = value;
}

export async function fetchBhgCalls() {
  if (isTestMode()) {
    const now = new Date();
    return {
      calls: TEST_CALLS,
      scheduleSummary: {
        todayIsoDate: now.toISOString().slice(0, 10),
        tomorrowIsoDate: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        today: { scheduledLabels: ["Drug Screen"], unscheduledLabels: [] },
        tomorrow: { scheduledLabels: [], unscheduledLabels: [] },
        monthlyScreen: null,
      },
      updatedAt: now.toISOString(),
    };
  }

  const response = await fetch("/api/website/bhg-calls", {
    headers: {
      "x-session-token": getSessionToken(),
    },
  });
  if (!response.ok) {
    throw new Error("Failed to load call records.");
  }

  const payload = (await response.json()) as CallsResponse;
  return {
    calls: payload.calls,
    scheduleSummary: payload.scheduleSummary,
    ...(typeof payload.retentionDays === "number" ? { retentionDays: payload.retentionDays } : {}),
    updatedAt: payload.updatedAt,
  };
}

export function subscribeBhgCalls(
  onUpdate: (
    calls: BhgCallRecord[],
    scheduleSummary: BhgScheduleSummary,
    updatedAt: string
  ) => void,
  onError: () => void
) {
  if (isTestMode()) {
    return () => undefined;
  }

  const token = encodeURIComponent(getSessionToken());
  const eventSource = new EventSource(
    resolveApiUrl(`/api/website/bhg-calls/stream?sessionToken=${token}`)
  );
  let closed = false;
  let errorNotified = false;

  const closeIfNeeded = () => {
    if (closed) {
      return;
    }
    closed = true;
    eventSource.close();
  };

  // Notify the consumer of a disconnect at most once per outage, WITHOUT closing
  // the EventSource — the browser reconnects automatically (and the server caps
  // stream lifetime to force periodic reconnects). Closing here would defeat
  // that and strand the dashboard on stale data. The flag re-arms when a message
  // arrives, so a later outage notifies again.
  const notifyErrorOnce = () => {
    if (errorNotified) {
      return;
    }
    errorNotified = true;
    onError();
  };

  eventSource.onmessage = (event) => {
    if (closed) {
      return;
    }
    // A message means the stream is live again; clear the outage latch so the
    // next disconnect re-notifies (and the consumer can exit fallback polling).
    errorNotified = false;
    try {
      const payload = JSON.parse(event.data) as {
        calls: BhgCallRecord[];
        scheduleSummary: BhgScheduleSummary;
        updatedAt: string;
      };
      onUpdate(payload.calls, payload.scheduleSummary, payload.updatedAt);
    } catch {
      // Malformed payload is a real error, but the connection is still open;
      // surface it without tearing down the auto-reconnecting stream.
      notifyErrorOnce();
    }
  };
  eventSource.onerror = () => {
    if (closed) {
      return;
    }
    notifyErrorOnce();
  };

  return () => {
    closeIfNeeded();
  };
}

export function buildBhgRecordingDownloadUrl(callSid: string) {
  const token = encodeURIComponent(getSessionToken());
  return resolveApiUrl(
    `/api/website/bhg-calls/${encodeURIComponent(callSid)}/recording?sessionToken=${token}`
  );
}

export async function fetchBhgRecordingAvailability(callSid: string): Promise<{
  available: boolean;
  note: string | null;
}> {
  if (isTestMode()) {
    return { available: true, note: null };
  }

  const cacheKey = callSid.trim();
  if (!cacheKey) {
    return {
      available: false,
      note: "Audio availability could not be confirmed.",
    };
  }

  const cached = recordingAvailabilityCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const response = await fetch(
    `/api/website/bhg-calls/${encodeURIComponent(cacheKey)}/recording-availability`,
    {
      headers: {
        "x-session-token": getSessionToken(),
      },
    }
  );
  if (!response.ok) {
    const unavailable = {
      available: false,
      note: "Audio availability could not be confirmed.",
    };
    recordingAvailabilityCache.set(cacheKey, unavailable);
    return unavailable;
  }

  const payload = (await response.json()) as {
    ok: boolean;
    available?: boolean;
    note?: string | null;
  };
  const result = {
    available: Boolean(payload.available),
    note: payload.note ?? null,
  };
  recordingAvailabilityCache.set(cacheKey, result);
  return result;
}

export async function fetchBhgCallEvents(callSid: string) {
  if (isTestMode()) {
    return {
      events: [
        {
          id: 1,
          callSid,
          createdAt: new Date().toISOString(),
          callStatus: "in-progress",
          callDurationSeconds: null,
          transcript: "please enter your patient id",
          payloadKeys: ["CallSid", "CallStatus", "UnstableSpeechResult"],
        },
      ] satisfies BhgCallEvent[],
      updatedAt: new Date().toISOString(),
    };
  }

  const response = await fetch(`/api/website/bhg-calls/${encodeURIComponent(callSid)}/events`, {
    headers: {
      "x-session-token": getSessionToken(),
    },
  });
  if (!response.ok) {
    throw new Error("Failed to load call event details.");
  }

  const payload = (await response.json()) as CallEventsResponse;
  return {
    events: payload.events,
    updatedAt: payload.updatedAt,
  };
}

export async function fetchBhgSelfReportedScreens(): Promise<BhgSelfReportedScreen[]> {
  if (isTestMode()) {
    return [];
  }
  const response = await fetch("/api/website/bhg-screens", {
    headers: { "x-session-token": getSessionToken() },
  });
  if (!response.ok) {
    throw new Error("Failed to load your screen records.");
  }
  const data = (await response.json()) as { screens?: BhgSelfReportedScreen[] };
  return data.screens ?? [];
}

/**
 * Record that the patient went in for a screen. Omitting `screenDate` lets the
 * server use the patient's own calendar day rather than the browser's.
 */
export async function recordBhgSelfReportedScreen(input: {
  screenDate?: string;
  note?: string;
}): Promise<{ ok: true; screen: BhgSelfReportedScreen } | { ok: false; error: string }> {
  if (isTestMode()) {
    return {
      ok: true,
      screen: {
        id: 1,
        screenDate: input.screenDate ?? new Date().toISOString().slice(0, 10),
        reportedAt: new Date().toISOString(),
        note: input.note ?? null,
        voidedAt: null,
      },
    };
  }

  const response = await fetch("/api/website/bhg-screens", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-session-token": getSessionToken(),
    },
    body: JSON.stringify(input),
  });
  const data = (await response.json().catch(() => null)) as {
    ok?: boolean;
    screen?: BhgSelfReportedScreen;
    error?: string;
  } | null;

  if (!response.ok || !data?.ok || !data.screen) {
    return { ok: false, error: data?.error ?? "Could not save your screen record." };
  }
  return { ok: true, screen: data.screen };
}

/** Mark a previously recorded screen as a mistake. The row is kept, not deleted. */
export async function voidBhgSelfReportedScreen(id: number): Promise<boolean> {
  if (isTestMode()) {
    return true;
  }
  const response = await fetch(`/api/website/bhg-screens/${encodeURIComponent(String(id))}/void`, {
    method: "POST",
    headers: { "x-session-token": getSessionToken() },
  });
  return response.ok;
}

export async function fetchBhgClinics(): Promise<BhgClinicOption[]> {
  if (isTestMode()) {
    return [];
  }
  const response = await fetch("/api/website/bhg-clinics", {
    headers: { "x-session-token": getSessionToken() },
  });
  if (!response.ok) {
    return [];
  }
  const data = (await response.json()) as { clinics?: BhgClinicOption[] };
  return data.clinics ?? [];
}

export async function fetchBhgSubscription(): Promise<BhgSubscriptionInfo | null> {
  if (isTestMode()) {
    return {
      products: [BHG_OFFERING_ID],
      bhgEnrollment: {
        userId: 1,
        clinicId: null,
        clinicName: null,
        notificationTime: "05:00",
        notificationTimezone: "America/New_York",
        mobilePhone: "3035550000",
        notificationEmail: "user@example.com",
        notificationChannels: ["sms"],
        clinicDosingHours: null,
        clinicOperatingHours: null,
      },
    };
  }

  const accessResponse = await fetch("/api/website/bhg-access", {
    headers: { "x-session-token": getSessionToken() },
  });
  if (!accessResponse.ok) {
    return null;
  }
  const accessData = (await accessResponse.json()) as {
    ok: boolean;
    hasAccess?: boolean;
  };
  if (!accessData.ok || !accessData.hasAccess) {
    return null;
  }

  const response = await fetch("/api/website/subscriptions", {
    headers: { "x-session-token": getSessionToken() },
  });
  if (!response.ok) {
    return null;
  }
  const data = (await response.json()) as {
    ok: boolean;
    products: string[];
    enrollmentSettings: Record<string, unknown>;
  };
  return {
    products: data.products,
    bhgEnrollment: (() => {
      const enrollment = data.enrollmentSettings?.[BHG_OFFERING_ID] as
        | BhgSubscriptionInfo["bhgEnrollment"]
        | null
        | undefined;
      if (!enrollment) {
        return null;
      }
      return {
        ...enrollment,
        notificationTime: convertBhgNotificationTimeToLocal(
          enrollment.notificationTime,
          enrollment.notificationTimezone
        ),
      };
    })(),
  };
}

export async function updateBhgClinic(clinicId: number | null): Promise<boolean> {
  if (isTestMode()) {
    return true;
  }
  const response = await fetch("/api/website/bhg-enrollment/clinic", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "x-session-token": getSessionToken(),
    },
    body: JSON.stringify({ clinicId }),
  });
  return response.ok;
}

export async function updateBhgNotifications(input: {
  mobilePhone: string;
  notificationEmail: string;
  notificationChannels: ("sms" | "email")[];
}): Promise<boolean> {
  if (isTestMode()) {
    return true;
  }
  const response = await fetch("/api/website/bhg-enrollment/notifications", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "x-session-token": getSessionToken(),
    },
    body: JSON.stringify(input),
  });
  return response.ok;
}

export async function updateBhgNotificationSchedule(input: {
  notificationTime: string;
  notificationTimezone: string;
}): Promise<boolean> {
  if (isTestMode()) {
    return true;
  }
  const utcNotificationTime = convertBhgNotificationTimeToUtc(
    input.notificationTime,
    input.notificationTimezone
  );
  const response = await fetch("/api/website/bhg-enrollment/schedule", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "x-session-token": getSessionToken(),
    },
    body: JSON.stringify({
      notificationTime: utcNotificationTime,
      notificationTimezone: input.notificationTimezone,
    }),
  });
  return response.ok;
}

export async function fetchUserProfile(): Promise<{ email: string; phoneNumber: string } | null> {
  if (isTestMode()) {
    return null;
  }
  const response = await fetch("/api/auth/session", {
    headers: { "x-session-token": getSessionToken() },
  });
  if (!response.ok) {
    return null;
  }
  const data = (await response.json()) as {
    ok: boolean;
    user?: { email: string | null; phoneNumber: string };
  };
  if (!data.ok || !data.user) {
    return null;
  }
  return { email: data.user.email ?? "", phoneNumber: data.user.phoneNumber };
}

export async function fetchBhgNotificationChannelAvailability(): Promise<{
  enabledChannels: NotificationChannel[];
}> {
  if (isTestMode()) {
    return { enabledChannels: ["sms", "email"] };
  }

  const response = await fetch("/api/website/bhg-notification-channels", {
    headers: { "x-session-token": getSessionToken() },
  });
  if (!response.ok) {
    return { enabledChannels: ["sms", "email"] };
  }

  const data = (await response.json()) as {
    ok: boolean;
    enabledChannels?: string[];
  };
  const enabled = (data.enabledChannels ?? []).filter(
    (channel): channel is NotificationChannel => channel === "sms" || channel === "email"
  );
  return { enabledChannels: enabled };
}

function isTestMode() {
  if (testModeOverride != null) {
    return testModeOverride;
  }
  return "__vitest_worker__" in (globalThis as Record<string, unknown>);
}

export const liveBhgCallDashboardProvider: BhgCallDashboardProvider = {
  fetchCalls: fetchBhgCalls,
  subscribeCalls: subscribeBhgCalls,
  fetchRecordingAvailability: fetchBhgRecordingAvailability,
  buildRecordingDownloadUrl: buildBhgRecordingDownloadUrl,
  fetchCallEvents: fetchBhgCallEvents,
  fetchClinics: fetchBhgClinics,
  fetchSubscription: fetchBhgSubscription,
  fetchUserProfile,
  fetchNotificationChannelAvailability: fetchBhgNotificationChannelAvailability,
  updateClinic: updateBhgClinic,
  updateNotifications: updateBhgNotifications,
  updateNotificationSchedule: updateBhgNotificationSchedule,
  fetchSelfReportedScreens: fetchBhgSelfReportedScreens,
  recordSelfReportedScreen: recordBhgSelfReportedScreen,
  voidSelfReportedScreen: voidBhgSelfReportedScreen,
};
